import { useLocation, useNavigate } from "react-router-dom";
import { MouseEvent, useEffect, useState } from "react";

import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  AppBar,
  ListItemButton,
  Backdrop,
  CircularProgress,
  Menu,
  Tooltip,
  Chip,
  Avatar,
  MenuItem,
  Link,
  capitalize,
} from "@mui/material";
import AdminIcon from "@mui/icons-material/SupervisorAccount";
import PersonIcon from "@mui/icons-material/Person";
import { Dashboard } from "@mui/icons-material";
import { Menu as MenuIcon } from "@mui/icons-material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SportsMotorsportsIcon from "@mui/icons-material/SportsMotorsports";

import conf from "../../conf";
import LOGO from "../../semiauth.logo.png";
import Footer from "../footer/Footer";
import { INIT_USER, useAuth } from "./AuthProvider";
import { useVaultSession, useVaultSignout } from "../../hooks/vault/base";

const OnlineIcon = <FiberManualRecordIcon color="success" fontSize="small" />;

const ProtectedRoute = ({ children }: { children: any }) => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const icon =
    auth.user.activeOrganization.role === "sys.admin" ? (
      <SportsMotorsportsIcon />
    ) : auth.user.activeOrganization.role === "org.admin" ? (
      <AdminIcon />
    ) : (
      <PersonIcon />
    );
  const tiles = conf.TILES[auth.user.activeOrganization.role];
  const currentPath = location.pathname;

  const session = useVaultSession();
  const signout = useVaultSignout();

  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem("SEMIAUTH_SIDEBAR");
    return saved === null ? true : saved === "true";
  });

  const toggleDrawer = () => {
    setOpen((prev) => {
      const newVal = !prev;
      localStorage.setItem("SEMIAUTH_SIDEBAR", newVal.toString());
      return newVal;
    });
  };

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenUserMenu = (e: MouseEvent<HTMLElement>) => {
    setAnchorElUser(e.currentTarget);
  };

  const handleCloseUserMenu = () => setAnchorElUser(null);

  const settings = [
    { title: "Profile", action: () => navigate("/profile") },
    { title: "Sign out", action: signout.mutate },
  ];

  useEffect(() => {
    if (auth.auth && auth.user.activeOrganization.status === "inactive")
      navigate("/connect");
  }, [auth.auth, auth.user.activeOrganization.status]);

  useEffect(() => {
    // redirect to signin
    if (session.isError) {
      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem("SEMIAUTH_REDIRECT_URL", currentPath); // store current (redirect) page

      localStorage.removeItem("SEMIAUTH_ORG_ID");
      localStorage.removeItem("SEMIAUTH_ORG_NAME");
      localStorage.removeItem("SEMIAUTH_AUTHENTICATED");

      auth.setAuth(false);
      auth.setUser(INIT_USER);

      navigate("/welcome");
    }
  }, [session.isError]);

  if (auth.isLoading && auth.isInitial) {
    return (
      <Backdrop
        open={true}
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer })}
      >
        <CircularProgress color="primary" />
      </Backdrop>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Link underline="none" href="/home">
            <img src={LOGO} alt="[semiauth] logo" className="logo-nav" />
          </Link>

          <Box sx={{ flexGrow: 1 }} />

          {auth.user.activeOrganization.status === "active" && (
            <Typography
              noWrap
              component="div"
              fontStyle="italic"
              marginRight={2}
            >
              {auth.user.activeOrganization.name}
            </Typography>
          )}

          <Box sx={{ flexGrow: 0 }}>
            <Tooltip
              title={
                <Chip
                  size="small"
                  icon={OnlineIcon}
                  label={auth?.user?.email}
                  sx={{ color: "inherit", bgcolor: "transparent" }}
                />
              }
            >
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar
                  sx={{ color: "primary.main", bgcolor: "secondary.main" }}
                >
                  {icon}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              keepMounted
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map(({ title, action }) => (
                <MenuItem
                  key={title}
                  onClick={() => {
                    action();
                    handleCloseUserMenu();
                  }}
                >
                  <Typography color="text.primary" textAlign="center">
                    {title}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        open={open}
        variant="persistent"
        sx={{
          width: conf.STYLE.WIDTH.DRAWER,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: conf.STYLE.WIDTH.DRAWER,
            boxSizing: "border-box",
          },
        }}
      >
        <Toolbar />

        <List>
          <ListItemButton
            key={"home"}
            onClick={() => navigate("/home")}
            selected={currentPath.includes("home")}
          >
            <ListItemIcon>
              <Dashboard />
            </ListItemIcon>
            <ListItemText primary={"Home"} />
          </ListItemButton>

          {tiles.map((tile) => (
            <ListItemButton
              key={tile.key}
              disabled={tile.disabled}
              onClick={() => navigate(tile.path)}
              selected={currentPath.includes(tile.key)}
            >
              <ListItemIcon>{tile.icon}</ListItemIcon>
              <ListItemText primary={capitalize(tile.key)} />
            </ListItemButton>
          ))}
        </List>

        <Footer />
      </Drawer>

      <Box
        component="main"
        sx={{
          p: 2,
          flexGrow: 1,
          position: "relative",
          marginTop: conf.STYLE.HEIGHT.NAVBAR,
          marginLeft: open ? 0 : `-${conf.STYLE.WIDTH.DRAWER}px`,
          transition: "margin 0.3s",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ProtectedRoute;
